import {
    CloseButton,
    DatePicker,
    IconButton,
    Portal,
    parseDate,
} from "@chakra-ui/react";
import { getLocalTimeZone } from "@internationalized/date";
import { isValid, parse } from "date-fns";
import { de } from "date-fns/locale/de";
import { Controller } from "react-hook-form";
import { BiCalendar } from "react-icons/bi";
import { forwardRef } from "react";
import { formatIso8601, localTimezone, parseBoardDue, parseIso } from "src/data/DateTimeUtil";
import { Field } from "@/components/ui/field";
import { InputGroup } from "@/components/ui/input-group";

const parseSafeIso = (v: Date): Date | undefined => {
    try {
        return parseIso(v);
    } catch {
        return undefined;
    }
};

export const formatDate = (v: Date | null): Date | undefined => v ? parseSafeIso(v) : undefined;

type ChakraDatePickerCompatProps = {
    dateFormat?: string;
    isClearable?: boolean;
    locale?: string | { code?: string };
    maxDate?: Date | null;
    minDate?: Date | null;
    name?: string;
    onChange?: (date: Date | null) => void;
    placeholderText?: string;
    selected?: Date | null;
    showPopperArrow?: boolean;
    disabled?: boolean;
    [key: string]: any;
};

const resolveLocale = (locale?: ChakraDatePickerCompatProps["locale"]) => {
    if (typeof locale === "string" && locale.length > 0) {
        return locale;
    }

    if (locale && typeof locale === "object" && typeof locale.code === "string") {
        return locale.code;
    }

    return de.code;
};

const toDateValue = (value?: Date | null) => {
    if (!value) {
        return [];
    }

    return [parseDate(formatIso8601(value))];
};

const parseInputValue = (rawValue: string, fallback?: Date | null) => {
    if (!rawValue?.trim()) {
        return undefined;
    }

    const parsed = parseBoardDue(rawValue) ?? parse(rawValue, "P", fallback ?? new Date(), { locale: de });
    return isValid(parsed) ? parsed : fallback ?? undefined;
};

export const ChakraDatePickerCompat = forwardRef<HTMLInputElement, ChakraDatePickerCompatProps>(
    ({
        dateFormat: _dateFormat,
        isClearable = false,
        locale,
        maxDate,
        minDate,
        name,
        onChange,
        placeholderText,
        selected,
        showPopperArrow: _showPopperArrow,
        disabled,
        ...props
    }, ref) => {
        const inputProps = props;

        return (
            <DatePicker.Root
                closeOnSelect
                disabled={disabled ?? inputProps.disabled}
                locale={resolveLocale(locale)}
                max={maxDate ? parseDate(formatIso8601(maxDate)) : undefined}
                min={minDate ? parseDate(formatIso8601(minDate)) : undefined}
                name={name}
                onValueChange={({ value }) => onChange?.(value[0] ? value[0].toDate(getLocalTimeZone()) : null)}
                parse={(rawValue) => {
                    const parsed = parseInputValue(rawValue, selected);
                    return parsed ? parseDate(formatIso8601(parsed)) : undefined;
                }}
                placeholder={placeholderText ?? "dd.MM.yyyy"}
                positioning={{ sameWidth: true }}
                timeZone={localTimezone}
                value={toDateValue(selected)}
            >
                <DatePicker.Control asChild>
                    <InputGroup
                        width={inputProps.width ?? inputProps.w ?? "full"}
                        endElement={
                            <>
                                {isClearable ? (
                                    <DatePicker.ClearTrigger asChild>
                                        <CloseButton
                                            me="-1"
                                            size="2xs"
                                            variant="ghost"
                                        />
                                    </DatePicker.ClearTrigger>
                                ) : null}
                                <DatePicker.Trigger asChild>
                                    <IconButton
                                        aria-label="Datum auswahlen"
                                        me="-2"
                                        size="xs"
                                        variant="ghost"
                                    >
                                        <BiCalendar />
                                    </IconButton>
                                </DatePicker.Trigger>
                            </>
                        }
                    >
                        <DatePicker.Input
                            autoComplete="off"
                            fixOnBlur
                            ref={ref}
                            {...inputProps}
                        />
                    </InputGroup>
                </DatePicker.Control>
                <Portal>
                    <DatePicker.Positioner>
                        <DatePicker.Content w="auto">
                            <DatePicker.View view="day">
                                <DatePicker.Header />
                                <DatePicker.DayTable />
                            </DatePicker.View>
                            <DatePicker.View view="month">
                                <DatePicker.Header />
                                <DatePicker.MonthTable />
                            </DatePicker.View>
                            <DatePicker.View view="year">
                                <DatePicker.Header />
                                <DatePicker.YearTable />
                            </DatePicker.View>
                        </DatePicker.Content>
                    </DatePicker.Positioner>
                </Portal>
            </DatePicker.Root>
        );
    }
);

ChakraDatePickerCompat.displayName = "ChakraDatePickerCompat";

const DateSelectController = ({
    control,
    label,
    name,
    clearable,
    parseValue,
    serializeValue,
    ...props
}: {
    control,
    label: string,
    name: string,
    clearable: boolean,
    parseValue?: (value: unknown) => Date | undefined,
    serializeValue?: (date: Date | null) => unknown,
    [key: string]: any
}) => {
    const mandatory = props['required'] ?? false;
    return (<Controller control={control} name={name} 
        rules={{ required: mandatory && `${label} auswählen`, validate: (value) => !mandatory || value !== null }}
        render={({ field: { value, name, onChange }, fieldState: { error, invalid } }) => (
        <Field id={name} label={label} required={mandatory} disabled={props['disabled'] ?? false} invalid={invalid} errorText={error?.message}>
            <ChakraDatePickerCompat
                id={name}
                isClearable={clearable}
                selected={parseValue ? parseValue(value) : formatDate(value)}
                onChange={(date: Date | null) => onChange(serializeValue ? serializeValue(date) : date)}
                dateFormat='dd.MM.yyyy'
                locale={de}
                showPopperArrow={true}
                {...props}
            />
        </Field>
    )} />)
}

export default DateSelectController
